// =============================================================================
// useBrandAssets — logo (SVG) + paleta brandowa per mapa (tabela map_brand_assets).
// Favicony NIE sa przechowywane — liczy je faviconBuilder z logo_svg przy pobieraniu.
// =============================================================================

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { MapBrandAssets, RgbColor } from "@/types/brand";

export function useBrandAssets(mapId: string | undefined) {
  const [assets, setAssets] = useState<MapBrandAssets | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!mapId) return;
    setLoading(true);
    try {
      const { data } = await (supabase as any)
        .from("map_brand_assets")
        .select("*")
        .eq("map_id", mapId)
        .maybeSingle();
      setAssets(data ?? null);
    } finally {
      setLoading(false);
    }
  }, [mapId]);

  useEffect(() => { void load(); }, [load]);

  const save = useCallback(
    async (fields: {
      logo_svg?: string | null;
      logo_prompt?: string | null;
      theme_color?: string | null;
      colors?: RgbColor[];
    }) => {
      if (!mapId) return;
      setSaving(true);
      try {
        const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
        // "in fields" zamiast ?? — null musi przejsc (czyszczenie logo/promptu)
        const pick = <K extends keyof typeof fields>(key: K) =>
          key in fields ? fields[key] ?? null : (assets as any)?.[key] ?? null;

        const row = {
          map_id: mapId,
          user_id: userId,
          logo_svg: pick("logo_svg"),
          logo_prompt: pick("logo_prompt"),
          theme_color: pick("theme_color"),
          colors: "colors" in fields ? fields.colors ?? [] : assets?.colors ?? [],
          updated_at: new Date().toISOString(),
        };
        await (supabase as any).from("map_brand_assets").upsert(row, { onConflict: "map_id" });
        await load();
      } finally {
        setSaving(false);
      }
    },
    [mapId, assets, load],
  );

  /** Kasuje logo I prompt — generator wraca do etapu 1. */
  const clear = useCallback(
    () => save({ logo_svg: null, logo_prompt: null, theme_color: null, colors: [] }),
    [save],
  );

  return { assets, loading, saving, save, clear, refresh: load };
}
